import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Package, ShoppingCart, DollarSign, Ticket, TrendingUp, ArrowUpRight } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div variants={fadeUp} transition={{ duration: 0.4, delay }}
      className="stat-card group cursor-default" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={20} strokeWidth={1.5} style={{ color }} />
        </div>
        <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-0 group-hover:opacity-50 transition-opacity" style={{ color: '#1A1A1A' }} />
      </div>
      <p className="font-sans text-sm mb-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{label}</p>
      <p className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>{value}</p>
    </motion.div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0, active_coupons: 0 });

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <motion.div initial="hidden" animate="visible" variants={stagger}>
        <motion.div variants={fadeUp} className="mb-10">
          <p className="label-accent mb-2">{t('admin.dashboard.welcome')}</p>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight" style={{ color: '#1A1A1A' }}>
            {user?.name || 'Admin'}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard icon={Package} label={t('admin.dashboard.products')} value={stats.products} color="#2C2C2C" delay={0} />
          <StatCard icon={ShoppingCart} label={t('admin.dashboard.orders')} value={stats.orders} color="#C4956A" delay={0.05} />
          <StatCard icon={DollarSign} label={t('admin.dashboard.revenue')} value={formatPrice(stats.revenue)} color="#4CAF50" delay={0.1} />
          <StatCard icon={Ticket} label={t('admin.dashboard.coupons')} value={stats.active_coupons} color="#E85D3A" delay={0.15} />
        </div>

        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.2 }}
          className="rounded-xl border p-6" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.05)' }}>
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp size={18} strokeWidth={1.5} style={{ color: '#C4956A' }} />
            <h2 className="font-serif text-xl" style={{ color: '#1A1A1A' }}>Quick Overview</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              <p className="font-sans text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(26,26,26,0.4)' }}>Store Status</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>Online</span>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              <p className="font-sans text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(26,26,26,0.4)' }}>Active Products</p>
              <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{stats.products} products listed</span>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              <p className="font-sans text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(26,26,26,0.4)' }}>Active Coupons</p>
              <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{stats.active_coupons} coupons running</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
